import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FiPhone, FiMapPin, FiArrowRight, FiClock
} from 'react-icons/fi';
import { supabase } from '../../../lib/supabase';
import { getVehicleUrl } from '../../../utils/vehicleSlug';

/**
 * Lightweight wrapper for location pages.
 * Handles: Helmet/SEO, breadcrumbs, inventory grid, bottom CTA.
 * All unique content comes through children.
 */
const LocationVehiclePage = ({ config, children }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const canonicalUrl = `https://paradise-performance.com/used-cars-${config.slug}`;

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const { data, error } = await supabase
        .from('vehicles_db2025')
        .select('*')
        .neq('status', 'sold')
        .order('created_at', { ascending: false })
        .limit(12);

      if (error) throw error;
      setVehicles(data || []);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'Call for Price';
    return `$${Number(price).toLocaleString()}`;
  };

  const getImageUrl = (vehicle) => {
    if (vehicle.images && vehicle.images.length > 0) {
      const img = vehicle.images[0];
      // Handle both string URLs and object format
      return typeof img === 'string' ? img : (img?.url || '/assets/placeholder-vehicle.jpg');
    }
    return '/assets/placeholder-vehicle.jpg';
  };

  const areaPages = [
    { name: 'Decatur', url: '/used-cars-decatur-tx' },
    { name: 'Bridgeport', url: '/used-cars-bridgeport-tx' },
    { name: 'Weatherford', url: '/used-cars-weatherford-tx' },
    { name: 'Denton', url: '/used-cars-denton-tx' },
    { name: 'Wise County', url: '/used-cars-wise-county' }
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen">
      <Helmet>
        <title>{config.title}</title>
        <meta name="description" content={config.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={config.ogTitle || config.title} />
        <meta property="og:description" content={config.ogDescription} />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={config.title} />
        <meta property="twitter:description" content={config.ogDescription} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoDealer",
            "name": "Paradise-Performance",
            "description": `Used cars and trucks serving ${config.city}, ${config.state}`,
            "url": canonicalUrl,
            "telephone": "+18173822316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 33.15,
              "longitude": -97.6836
            },
            "areaServed": [
              {
                "@type": "City",
                "name": config.city,
                "containedInPlace": {
                  "@type": "State",
                  "name": "Texas"
                }
              }
            ],
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "09:00",
                "closes": "18:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday",
                "opens": "10:00",
                "closes": "14:00"
              }
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": `Used Vehicles for ${config.city} Buyers`,
              "itemListElement": vehicles.slice(0, 6).map(v => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Car",
                  "name": `${v.year} ${v.make} ${v.model}`,
                  "manufacturer": v.make,
                  "model": v.model,
                  "vehicleModelDate": String(v.year)
                },
                "price": v.price,
                "priceCurrency": "USD"
              }))
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://paradise-performance.com/" },
              { "@type": "ListItem", "position": 2, "name": "Inventory", "item": "https://paradise-performance.com/inventory" },
              { "@type": "ListItem", "position": 3, "name": `Used Cars ${config.city}`, "item": canonicalUrl }
            ]
          })}
        </script>

        {config.faqs && config.faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": config.faqs.map(faq => ({
                "@type": "Question",
                "name": faq.q,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.a
                }
              }))
            })}
          </script>
        )}
      </Helmet>

      {/* Breadcrumb */}
      <nav className="bg-gray-100 py-3 px-4 text-sm text-gray-600">
        <div className="container mx-auto">
          <Link to="/" className="hover:text-primary-600">Home</Link>
          <span className="mx-2">›</span>
          <Link to="/inventory" className="hover:text-primary-600">Inventory</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-900 font-medium">Used Cars {config.city}</span>
        </div>
      </nav>

      {/* ─── City-Specific Content (injected by each page) ─── */}
      {children}

      {/* ─── Inventory Grid (shared) ─── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-3">
            What's on the Lot Right Now
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Every vehicle goes through our shop before it hits the lot. Inspected. Serviced. Ready to drive home.
          </p>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
              <p className="mt-4 text-gray-500">Loading inventory...</p>
            </div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">No vehicles listed right now. Check back soon or give us a call.</p>
              <a href="tel:+18173822316" className="text-amber-600 font-semibold hover:underline">
                (817) 382-2316
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.slice(0, 9).map((v, i) => (
                <motion.div
                  key={v.id}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="h-48 bg-gray-200 overflow-hidden">
                    <img
                      src={getImageUrl(v)}
                      alt={`${v.year} ${v.make} ${v.model} for sale near ${config.city}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {v.year} {v.make} {v.model}
                    </h3>
                    <p className="text-2xl font-bold text-amber-600 mb-2">
                      {formatPrice(v.price)}
                    </p>
                    {v.mileage && (
                      <p className="text-sm text-gray-500 mb-4">
                        {Number(v.mileage).toLocaleString()} miles
                      </p>
                    )}
                    <Link
                      to={getVehicleUrl(v)}
                      className="block w-full text-center bg-gray-900 text-white py-2.5 rounded-lg hover:bg-amber-600 transition-colors font-medium"
                    >
                      View Details
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Link
              to="/inventory"
              className="inline-flex items-center px-8 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors"
            >
              See Full Inventory <FiArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FAQ Section ─── */}
      {config.faqs && config.faqs.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-10">
              Common Questions From {config.city} Buyers
            </h2>

            <div className="max-w-3xl mx-auto space-y-4">
              {config.faqs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Bottom CTA ─── */}
      <section className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {config.ctaHeadline || `Come See Us From ${config.city}`}
          </h2>
          <p className="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">
            {config.ctaSubline || 'Financing for all credit types. Trade-ins welcome. Warranty on every vehicle.'}
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-3xl mx-auto border border-white/20">
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <Link
                to="/inventory"
                className="inline-flex items-center justify-center px-6 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-lg transition-colors"
              >
                Browse Inventory
              </Link>
              <a
                href="tel:+18173822316"
                className="inline-flex items-center justify-center px-6 py-4 border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors"
              >
                <FiPhone className="mr-2" /> (817) 382-2316
              </a>
            </div>

            <div className="text-white/90 text-sm space-y-2">
              <p className="flex items-center justify-center">
                <FiMapPin className="mr-2" /> 305 Co Rd 3470, Paradise, TX 76073
              </p>
              <p>
                <FiClock className="inline mr-2" />
                Mon-Fri 9AM-6PM • Sat 10AM-2PM
              </p>
            </div>
          </div>

          {/* Cross-links */}
          <div className="mt-12 max-w-4xl mx-auto">
            <p className="text-gray-400 text-sm mb-3">We also serve:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {areaPages.filter(a => a.name !== config.city).map(area => (
                <Link
                  key={area.name}
                  to={area.url}
                  className="text-sm text-white/80 hover:text-white bg-white/10 px-3 py-1 rounded-full transition-colors"
                >
                  Used Cars {area.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default LocationVehiclePage;
